import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { 
  BookOpen, Calendar, Clock, Target, FileText, 
  ChevronDown, ChevronRight, Printer, CheckCircle2,
  Lightbulb, ClipboardList, BookMarked
} from 'lucide-react';

const SubjectSchemeOfWork = ({ 
  subject = 'Subject',
  year = 11, 
  weeklyLessons = {},
  subjectColor = 'blue'
}) => {
  const [selectedTerm, setSelectedTerm] = useState('term1');
  const [expandedWeeks, setExpandedWeeks] = useState({});

  // Get year key format based on the data structure
  const getYearKey = () => {
    return `year${year}`;
  };

  // Get term data
  const yearData = weeklyLessons[getYearKey()] || {};
  const termData = yearData[selectedTerm]?.scheme || yearData[selectedTerm]?.weeks || [];

  const toggleWeek = (weekNum) => {
    setExpandedWeeks(prev => ({
      ...prev,
      [weekNum]: !prev[weekNum]
    }));
  };

  // Color mapping
  const colorMap = {
    'blue': { bg: 'bg-blue-50', border: 'border-blue-200', badge: 'bg-blue-100 text-blue-800', header: 'bg-blue-600', headerLight: 'bg-blue-100' },
    'green': { bg: 'bg-green-50', border: 'border-green-200', badge: 'bg-green-100 text-green-800', header: 'bg-green-600', headerLight: 'bg-green-100' },
    'purple': { bg: 'bg-purple-50', border: 'border-purple-200', badge: 'bg-purple-100 text-purple-800', header: 'bg-purple-600', headerLight: 'bg-purple-100' },
    'red': { bg: 'bg-red-50', border: 'border-red-200', badge: 'bg-red-100 text-red-800', header: 'bg-red-600', headerLight: 'bg-red-100' },
    'amber': { bg: 'bg-amber-50', border: 'border-amber-200', badge: 'bg-amber-100 text-amber-800', header: 'bg-amber-600', headerLight: 'bg-amber-100' },
    'teal': { bg: 'bg-teal-50', border: 'border-teal-200', badge: 'bg-teal-100 text-teal-800', header: 'bg-teal-600', headerLight: 'bg-teal-100' },
    'indigo': { bg: 'bg-indigo-50', border: 'border-indigo-200', badge: 'bg-indigo-100 text-indigo-800', header: 'bg-indigo-600', headerLight: 'bg-indigo-100' },
    'emerald': { bg: 'bg-emerald-50', border: 'border-emerald-200', badge: 'bg-emerald-100 text-emerald-800', header: 'bg-emerald-600', headerLight: 'bg-emerald-100' },
    'pink': { bg: 'bg-pink-50', border: 'border-pink-200', badge: 'bg-pink-100 text-pink-800', header: 'bg-pink-600', headerLight: 'bg-pink-100' }
  };

  const colors = colorMap[subjectColor] || colorMap['blue'];

  const printScheme = () => {
    const printWindow = window.open('', '_blank');
    const termName = selectedTerm.replace('term', 'Term ');
    
    printWindow.document.write(`
      <html>
      <head>
        <title>Scheme of Work - Year ${year} ${subject} - ${termName}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; max-width: 900px; margin: 0 auto; font-size: 12px; }
          h1 { color: #1e40af; border-bottom: 2px solid #1e40af; padding-bottom: 10px; font-size: 18px; }
          table { width: 100%; border-collapse: collapse; margin: 15px 0; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; vertical-align: top; }
          th { background: #e0e7ff; font-weight: bold; }
          .week-header { background: #f3f4f6; font-weight: bold; }
          .badge { display: inline-block; background: #dbeafe; color: #1e40af; padding: 2px 6px; border-radius: 4px; font-size: 10px; margin-right: 4px; }
          ul { margin: 5px 0; padding-left: 15px; }
          li { margin: 2px 0; }
          @media print { body { padding: 10px; } }
        </style>
      </head>
      <body>
        <h1>📚 Year ${year} ${subject} - Scheme of Work - ${termName}</h1>
        <table>
          <thead>
            <tr>
              <th width="8%">Week</th>
              <th width="12%">Strand</th>
              <th width="20%">Topic</th>
              <th width="30%">Learning Outcomes</th>
              <th width="15%">Activities</th>
              <th width="15%">Assessment</th>
            </tr>
          </thead>
          <tbody>
            ${termData.map(week => `
              <tr>
                <td class="week-header">Week ${week.week}</td>
                <td><span class="badge">${week.strand || 'General'}</span></td>
                <td><strong>${week.topic}</strong></td>
                <td>
                  <ul>
                    ${(week.learningOutcomes || []).map(outcome => `<li>${outcome}</li>`).join('')}
                  </ul>
                </td>
                <td>
                  <ul>
                    ${(week.activities || []).map(activity => `<li>${activity}</li>`).join('')}
                  </ul>
                </td>
                <td>${week.assessment || 'Ongoing assessment'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        <div style="margin-top: 20px; text-align: center; color: #666; font-size: 11px;">
          <p>🌴 Penama Education Portal - ${subject} Curriculum | Generated ${new Date().toLocaleDateString()}</p>
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
  };

  // Check if data exists
  if (!termData || termData.length === 0) {
    return (
      <Card className={`${colors.border} border-2`}>
        <CardHeader className={colors.headerLight}>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Year {year} {subject} - Scheme of Work
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <p className="text-gray-500">No scheme data available for Year {year}. Please check if the curriculum data has been loaded.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`${colors.border} border-2`}>
      <CardHeader className={colors.headerLight}>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Year {year} {subject} - Scheme of Work
          </CardTitle>
          <Button onClick={printScheme} variant="outline" size="sm" className="flex items-center gap-2">
            <Printer className="w-4 h-4" />
            Print Scheme
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-4">
        {/* Term Selector */}
        <Tabs value={selectedTerm} onValueChange={setSelectedTerm} className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-4">
            <TabsTrigger value="term1" className="flex items-center gap-1">
              <BookOpen className="w-4 h-4" />
              Term 1
            </TabsTrigger>
            <TabsTrigger value="term2" className="flex items-center gap-1">
              <BookOpen className="w-4 h-4" />
              Term 2
            </TabsTrigger>
            <TabsTrigger value="term3" className="flex items-center gap-1">
              <BookOpen className="w-4 h-4" />
              Term 3
            </TabsTrigger>
            <TabsTrigger value="term4" className="flex items-center gap-1">
              <BookOpen className="w-4 h-4" />
              Term 4
            </TabsTrigger>
          </TabsList>

          {['term1', 'term2', 'term3', 'term4'].map(term => (
            <TabsContent key={term} value={term} className="space-y-3">
              {(yearData[term]?.scheme || yearData[term]?.weeks || []).map((week, index) => (
                <Collapsible 
                  key={index}
                  open={expandedWeeks[week.week]}
                  onOpenChange={() => toggleWeek(week.week)}
                >
                  <Card className={`${colors.bg} border ${colors.border}`}>
                    <CollapsibleTrigger asChild>
                      <CardHeader className="cursor-pointer hover:bg-opacity-80 py-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {expandedWeeks[week.week] ? 
                              <ChevronDown className="w-5 h-5 text-gray-500" /> : 
                              <ChevronRight className="w-5 h-5 text-gray-500" />
                            }
                            <div>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className={colors.badge}>
                                  Week {week.week}
                                </Badge>
                                <span className="font-semibold text-gray-800">{week.topic}</span>
                              </div>
                              {week.strand && (
                                <p className="text-sm text-gray-500 mt-1">
                                  <span className="font-medium">Strand:</span> {week.strand}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {week.assessment && (
                              <Badge variant="secondary" className="text-xs">
                                <ClipboardList className="w-3 h-3 mr-1" />
                                {week.assessment}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </CardHeader>
                    </CollapsibleTrigger>
                    
                    <CollapsibleContent>
                      <CardContent className="pt-0 pb-4">
                        <div className="grid md:grid-cols-2 gap-4">
                          {/* Learning Outcomes */}
                          <div className="space-y-2">
                            <h4 className="font-semibold text-sm flex items-center gap-2 text-gray-700">
                              <Target className="w-4 h-4" />
                              Learning Outcomes
                            </h4>
                            <ul className="text-sm space-y-1 pl-4">
                              {(week.learningOutcomes || []).map((outcome, i) => (
                                <li key={i} className="flex items-start gap-2">
                                  <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                                  <span>{outcome}</span>
                                </li>
                              ))}
                            </ul>
                          </div>

                          {/* Activities */}
                          <div className="space-y-2">
                            <h4 className="font-semibold text-sm flex items-center gap-2 text-gray-700">
                              <Lightbulb className="w-4 h-4" />
                              Activities
                            </h4>
                            <ul className="text-sm space-y-1 pl-4">
                              {(week.activities || []).map((activity, i) => (
                                <li key={i} className="flex items-start gap-2">
                                  <BookMarked className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                                  <span>{activity}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>

                        {/* Resources */}
                        {week.resources && (
                          <div className="mt-4 pt-3 border-t">
                            <h4 className="font-semibold text-sm flex items-center gap-2 text-gray-700 mb-2">
                              <FileText className="w-4 h-4" />
                              Resources
                            </h4>
                            <div className="flex flex-wrap gap-2">
                              {(Array.isArray(week.resources) ? week.resources : [week.resources]).map((resource, i) => (
                                <Badge key={i} variant="outline" className="text-xs">
                                  {resource}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </CollapsibleContent>
                  </Card>
                </Collapsible>
              ))}
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default SubjectSchemeOfWork;
